import { FormEvent, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

type DocumentationPublishModalProps = {
  open: boolean;
  pageTitle: string;
  initialExpiresAtLocal?: string;
  allowExpiration?: boolean;
  onClose: () => void;
  onConfirm: (expiresAtUtc: string | null) => void;
};

/** Converte ISO UTC para valor de input datetime-local (horário local). */
export function toDatetimeLocalValue(iso?: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Converte datetime-local (sem timezone) para ISO UTC. */
export function fromDatetimeLocalValue(local: string): string | null {
  const trimmed = local.trim();
  if (!trimmed) return null;
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

export function DocumentationPublishModal({
  open,
  pageTitle,
  initialExpiresAtLocal = '',
  allowExpiration = true,
  onClose,
  onConfirm,
}: DocumentationPublishModalProps) {
  const [expiresLocal, setExpiresLocal] = useState(initialExpiresAtLocal);

  useEffect(() => {
    if (open) setExpiresLocal(initialExpiresAtLocal);
  }, [open, initialExpiresAtLocal]);

  if (!open) return null;

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    onConfirm(allowExpiration ? fromDatetimeLocalValue(expiresLocal) : null);
  }

  return createPortal(
    <div className="docs-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="docs-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="docs-publish-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="docs-publish-title" className="docs-modal__title">
          Publicar “{pageTitle}”
        </h3>
        <form onSubmit={handleSubmit} className="erp-form">
          {allowExpiration ? (
            <label className="erp-label-block">
              Data de expiração
              <input
                type="datetime-local"
                className="erp-input"
                value={expiresLocal}
                onChange={(e) => setExpiresLocal(e.target.value)}
              />
              <span className="mt-1 block text-xs text-secondary-500">
                Opcional. Se ficar vazio, a página só sai do ar por ação manual (arquivar).
              </span>
            </label>
          ) : (
            <p className="text-sm text-secondary-600">
              A página publicada permanece no histórico até ser arquivada manualmente.
            </p>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" className="erp-btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="erp-btn-primary">
              Publicar
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
